import { Component, Fragment, createRef } from 'react';
import { createPortal } from 'react-dom';
import { ThemeContext } from 'styled-components';

import { notify, cloneDeep } from 'libs/common';
import { ChangeStatus } from 'libs/ui';
import { addPaymentComment } from 'services/payments-service';
import { Comments } from './comments/comments';
import { AddComment } from './comments/add-comment';
import { Container, Option, OptionsPopup, OptionsSeparator, PopupContainer } from './with-popup.styled';

const FORM_GAP = 5;
const FORM_ESTIMATED_HEIGHT = {
  status: 260,
  'add-comment': 260,
};
const FORM_WIDTH = {
  status: 440,
  'add-comment': 440,
  'show-comments': 600,
};
const COMMENTS_HEADER_HEIGHT = 70;
const COMMENTS_BUTTONS_HEIGHT = 60;
const COMMENTS_TABLE_HEADER_HEIGHT = 30;
const COMMENTS_ROW_HEIGHT = 34;
const COMMENTS_EMPTY_HEIGHT = 40;
const COMMENTS_MAX_BLOCK_HEIGHT = 400;

const types = {
  rfid: 'rfids',
  csc: 'epms',
  plates: 'vehiclePlates',
};

const getRealType = (type) => {
  return types[type] ? types[type] : type;
};

export const withPopup = (WrappedComponent) => {
  return class WithPopup extends Component {
    static contextType = ThemeContext;

    constructor(props) {
      super(props);
      this.state = { showOptions: false, showForm: '', actionType: '' };

      this.containerRef = createRef();
      this.popupRef = createRef();
      this.formPopupRef = createRef();
    }

    componentDidUpdate(prevProps, prevState) {
      const wasOpen = prevState.showOptions || !!prevState.showForm;
      const isOpen = this.state.showOptions || !!this.state.showForm;
      if (wasOpen !== isOpen) {
        if (isOpen) {
          document.addEventListener('mousedown', this.handleOutsideClick, true);
          window.addEventListener('scroll', this.handleReposition, true);
          window.addEventListener('resize', this.handleReposition);
        } else {
          this.removeGlobalListeners();
        }
      }
    }

    componentWillUnmount() {
      this.removeGlobalListeners();
    }

    removeGlobalListeners = () => {
      document.removeEventListener('mousedown', this.handleOutsideClick, true);
      window.removeEventListener('scroll', this.handleReposition, true);
      window.removeEventListener('resize', this.handleReposition);
    };

    /**
     * close popup on click outside of the trigger and the (portaled) popup itself
     */
    handleOutsideClick = (event) => {
      const inTrigger = this.containerRef.current && this.containerRef.current.contains(event.target);
      const inPopup = this.popupRef.current && this.popupRef.current.contains(event.target);
      const inForm = this.formPopupRef.current && this.formPopupRef.current.contains(event.target);
      if (!inTrigger && !inPopup && !inForm) {
        this.hideOptions();
      }
    };

    hideOptions = () => {
      this.setState({ showOptions: false });
    };

    /**
     * the popups are positioned (once) relative to their trigger row via a portal,
     * so on scroll/resize their anchor may have moved — just close rather than track it
     */
    handleReposition = () => {
      if (this.state.showOptions) {
        this.hideOptions();
      }
      if (this.state.showForm) {
        this.closeForm();
      }
    };

    toggleOptions = () => {
      const show = this.state.showOptions;
      this.setState({ showOptions: !show });
    };

    /**
     * count options that pass isVisible, to estimate the popup's rendered height
     */
    getVisibleCount = (optionsArray) => {
      if (!Array.isArray(optionsArray)) {
        return 0;
      }
      return optionsArray.filter((item) => (item.isVisible ? item.isVisible(this.props.row) : true)).length;
    };

    /**
     * estimate the rendered height of the currently open form popup, to decide whether
     * it fits below the row or needs to flip above it
     */
    getEstimatedFormHeight = (showForm, row) => {
      if (showForm === 'show-comments') {
        const count = (row && row.comments && row.comments.length) || 0;
        const blockHeight = count ? Math.min(COMMENTS_TABLE_HEADER_HEIGHT + count * COMMENTS_ROW_HEIGHT, COMMENTS_MAX_BLOCK_HEIGHT) : COMMENTS_EMPTY_HEIGHT;
        return COMMENTS_HEADER_HEIGHT + blockHeight + COMMENTS_BUTTONS_HEIGHT;
      }
      return FORM_ESTIMATED_HEIGHT[showForm] || 0;
    };

    getOptions = (optionsArray) => {
      if (!Array.isArray(optionsArray)) {
        return null;
      }

      const cnt = optionsArray.length;
      const options = [];

      optionsArray.forEach((item, index) => {
        const last = index === cnt - 1;
        const show = item.isVisible ? item.isVisible(this.props.row) : true;
        if (show) {
          const onClick = (event) => {
            event.stopPropagation();
            event.preventDefault();
            this.optionOnClick(item.func || item.action, item.actionType);
          };
          options.push(
            <Fragment key={index}>
              <Option disabled={this.props.disabled} onClick={onClick}>
                {item.icon && <i className={item.icon} />}
                {item.getTitle ? item.getTitle(this.props.row) : item.text || item.title}
              </Option>
              {!last && <OptionsSeparator />}
            </Fragment>,
          );
        }
      });
      return options;
    };

    optionOnClick = (func, type) => {
      if (this.props.disabled) {
        return;
      } else {
        if (func === 'status') {
          // status change form
          this.setState({ showForm: 'status', actionType: type });
        } else if (func === 'add-comment' || func === 'show-comments') {
          // comments
          this.setState({ showForm: func, actionType: type });
        } else {
          // common
          if (func) {
            func(this.props.row);
          }
        }
        this.toggleOptions();
      }
    };

    closeForm = () => {
      this.setState({ showForm: '' });
    };

    /**
     * prepare row
     * @param {*} row
     * @returns
     */
    prepareRow = (row) => {
      return {
        id: row.id,
        parking_id: row.parking_id,
        value: row.mean_id,
        status: row.status,
        type: getRealType(row.type),
      };
    };

    /**
     * add comment
     */
    addComment = (data) => {
      // console.log('🚀 ~ withPopup ~ data:', data);
      const update = this.props.updateEntry;
      addPaymentComment(data)
        .then((comment) => {
          //closeForm();
          const out = cloneDeep(this.props.row);
          out.comment = data.comment;
          out.last_comment = data.comment;
          if (!out.comments) {
            out.comments = [];
          }
          out.comments.splice(0, 0, comment);
          // out.comments.push(comment);
          update(data.id, out);
          //refresh();
        })
        .catch((error) => {
          notify('Добавить комментарий', error.toString());
        });
    };

    render() {
      const { left, options, row } = this.props;
      const { showForm, actionType, showOptions } = this.state;

      // popup is portaled to document.body so it renders above the table instead of
      // being clipped by (and forcing a scrollbar on) the table's scrollable container
      const rect = showOptions && this.containerRef.current ? this.containerRef.current.getBoundingClientRect() : null;
      const isV2 = this.context && this.context.ui === 'v2';
      const v2RightMargin = isV2 ? 8 : 0;
      let popupStyle;
      let optionsArrowRight;
      let flip = false;
      if (rect) {
        const ITEM_HEIGHT = 45;
        const SEPARATOR_HEIGHT = 1;
        const GAP = 10;
        const visibleCount = this.getVisibleCount(options);
        const estimatedHeight = visibleCount > 0 ? visibleCount * ITEM_HEIGHT + (visibleCount - 1) * SEPARATOR_HEIGHT : 0;
        const spaceBelow = window.innerHeight - (rect.top + 35);
        flip = estimatedHeight > 0 && spaceBelow < estimatedHeight + GAP;
        popupStyle = {
          position: 'fixed',
          top: flip ? rect.top - estimatedHeight - GAP : rect.top + 35,
          left: rect.left + (rect.width * (left ?? 50)) / 100,
        };
        // new theme: right edge of the popup is aligned to the right edge of the table, the arrow stays on the trigger
        const tableEl = isV2 ? this.containerRef.current.closest('table') : null;
        if (tableEl) {
          const tableRight = tableEl.getBoundingClientRect().right;
          const triggerCenter = rect.left + rect.width / 2;
          optionsArrowRight = Math.max(tableRight - triggerCenter - 8, 12);
          popupStyle = { position: 'fixed', top: popupStyle.top, right: window.innerWidth - tableRight - v2RightMargin };
        }
      }

      // form popups (ChangeStatus / AddComment / Comments) are portaled to document.body too,
      // positioned above or below the triggering row (whichever fits), with an arrow pointing at it
      const formRect = showForm && this.containerRef.current ? this.containerRef.current.getBoundingClientRect() : null;
      let formPopupStyle;
      let formFlip = false;
      let formArrowOffset = 20;
      const dr1 = isV2 ? 0 : 23;

      if (formRect) {
        const estimatedHeight = this.getEstimatedFormHeight(showForm, row);
        const spaceBelow = window.innerHeight - (formRect.bottom + FORM_GAP);
        formFlip = estimatedHeight > 0 && spaceBelow < estimatedHeight;

        // align the popup to the right edge of the table itself (not the trigger button),
        // but keep the arrow pointing at the trigger's own horizontal center
        const tableEl = this.containerRef.current.closest('table');
        const anchorRight = tableEl ? tableEl.getBoundingClientRect().right + dr1 : formRect.right;
        // console.log('anchorRight', anchorRight, formRect);
        const popupWidth = FORM_WIDTH[showForm] || formRect.width;
        const triggerCenter = formRect.left + formRect.width / 2;
        formArrowOffset = Math.min(Math.max(anchorRight - triggerCenter - 8, 12), popupWidth - 28) - dr1;

        formPopupStyle = {
          right: window.innerWidth - anchorRight - v2RightMargin,
          ...(formFlip ? { bottom: window.innerHeight - formRect.top + FORM_GAP } : { top: formRect.bottom + FORM_GAP }),
        };
      }
      // console.log("formPopupStyle", formPopupStyle);

      return (
        <Container ref={this.containerRef}>
          <WrappedComponent popupDisplayHandler={this.toggleOptions} {...this.props} />
          {rect &&
            createPortal(
              <OptionsPopup ref={this.popupRef} left={left} $flip={flip} $arrowRight={optionsArrowRight} style={popupStyle}>
                {this.getOptions(options)}
              </OptionsPopup>,
              document.body,
            )}
          {this.props.children}
          {formRect &&
            createPortal(
              <PopupContainer ref={this.formPopupRef} $flip={formFlip} $arrowOffset={formArrowOffset} style={formPopupStyle}>
                {showForm === 'status' && <ChangeStatus updateEntry={this.props.updateEntry} type={actionType} close={this.closeForm} info={row} />}
                {showForm === 'add-comment' && <AddComment addComment={this.addComment} type="addComment" removeFormInfo={this.closeForm} info={this.prepareRow(row)} />}
                {showForm === 'show-comments' && <Comments type="comments" removeFormInfo={this.closeForm} comments={row.comments} />}
              </PopupContainer>,
              document.body,
            )}
        </Container>
      );
    }
  };
};
